import * as statisticsService from "@features/dashboard/services/statisticsService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import { API_CONFIG } from "@shared/utils/constants";

export function useStatstics(city = "all", user = "all") {
  const { data: UserStatistics, isLoading } = useQuery({
    queryKey: ["userStatistics"],
    queryFn: statisticsService.getUserStatistics,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const { data: PackageStatusCount, isLoading: isLoadingPackageStatusCount } =
    useQuery({
      queryKey: ["packageStatusCount", { city, user }],
      queryFn: () => statisticsService.getPackageStatusCount({ city, user }),
      staleTime: 2 * 60 * 1000, // 2 minutes
      refetchOnWindowFocus: false,
    });

  return {
    UserStatistics,
    PackageStatusCount,
    isLoadingPackageStatusCount,
    isLoading,
  };
}

/**
 * Enhanced useOverview hook with better error handling, data transformation, and performance
 * @param {Object} params - Query parameters
 * @param {Date} params.from - Start date
 * @param {Date} params.to - End date
 * @param {string} params.city - City filter (optional)
 * @param {string} params.user - User filter (optional, admin only)
 * @param {Object} options - Additional options
 * @param {boolean} options.enabled - Whether the query should be enabled
 * @param {number} options.staleTime - Cache time in milliseconds
 * @param {boolean} options.refetchOnWindowFocus - Whether to refetch on window focus
 */
export const useOverview = (params = {}, options = {}) => {
  const queryClient = useQueryClient();

  // Normalize and validate parameters
  const normalizedParams = useMemo(() => {
    const { from, to, city, user, ...rest } = params;

    // Validate required parameters
    if (!from || !to) {
      return null;
    }

    // Ensure dates are properly formatted
    const normalizedFrom = from instanceof Date ? from.toISOString() : from;
    const normalizedTo = to instanceof Date ? to.toISOString() : to;

    const searchParams = new URLSearchParams({
      from: normalizedFrom,
      to: normalizedTo,
      ...rest,
    });

    // Add optional parameters
    if (city && city !== "all") searchParams.append("city", city);
    if (user && user !== "all") searchParams.append("user", user);

    return searchParams.toString();
  }, [params]);

  const { data, isLoading, isError, error, refetch, isFetching, isRefetching } =
    useQuery({
      queryKey: ["overview", normalizedParams],
      queryFn: async () => {
        try {
          const response = await statisticsService.getOverview(
            normalizedParams
          );
          return response;
        } catch (err) {
          // Log error for debugging
          console.error("Overview fetch error:", err);

          // Show user-friendly error message
          const errorMessage =
            err.response?.data?.message || "Failed to fetch overview data";
          toast.error(errorMessage);

          throw err;
        }
      },
      enabled: !!normalizedParams && options.enabled !== false,
      staleTime: options.staleTime || 3 * 60 * 1000, // 3 minutes default
      refetchOnWindowFocus: options.refetchOnWindowFocus ?? false,
      retry: (failureCount, error) => {
        // Only retry on server errors (5xx) or network errors
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        // Retry up to 3 times for server errors
        return failureCount < API_CONFIG.MAX_RETRY_ATTEMPTS;
      },
      retryDelay: (attemptIndex) =>
        Math.min(
          API_CONFIG.RETRY_DELAY_MS * 2 ** attemptIndex,
          API_CONFIG.MAX_RETRY_DELAY_MS
        ),
    });

  // Transform and normalize data for easier consumption
  const transformedData = useMemo(() => {
    if (!data?.data) return null;

    const overview = data.data;
    console.log("Server Overview Data:", overview);

    // Return server data directly with some convenient computed fields
    return {
      ...overview,

      // Add computed fields for backward compatibility
      packages: {
        ...overview.packages,
        total: overview.packages?.totalPackagesInRange || 0,
        delivered: overview.packages?.totalDelivered || 0,
      },

      // Add computed amounts for easier access
      amounts: {
        ...overview.amounts,
        totalAmount: overview.amounts?.totalRevenue || 0,
        totalProfit: overview.amounts?.profit || 0,
      },

      // Add recent packages alias
      recentPackages: overview.lastPackages || [],

      // Keep raw data accessible
      raw: overview,
    };
  }, [data]);

  // Enhanced refresh function with optimistic updates
  const enhancedRefetch = async (showToast = true) => {
    try {
      if (showToast) {
        toast.loading("Refreshing data...", { id: "overview-refresh" });
      }

      const result = await refetch();

      if (showToast) {
        toast.success("Data refreshed successfully", {
          id: "overview-refresh",
        });
      }

      return result;
    } catch (error) {
      if (showToast) {
        toast.error("Failed to refresh data", { id: "overview-refresh" });
      }
      throw error;
    }
  };

  // Invalidate related queries when data changes
  const invalidateRelatedQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["packages"] });
    queryClient.invalidateQueries({ queryKey: ["invoices"] });
    queryClient.invalidateQueries({ queryKey: ["userStatistics"] });
  };

  return {
    // Data
    data: transformedData,
    rawData: data,

    // Loading states
    isLoading,
    isFetching,
    isRefetching,

    // Error states
    isError,
    error,

    // Actions
    refetch: enhancedRefetch,
    invalidateRelatedQueries,

    // Helpers
    hasData: !!transformedData,
    isEmpty:
      transformedData &&
      Object.values(transformedData.packages || {}).every((val) => val === 0),
  };
};
